/* eslint-disable @typescript-eslint/indent */
import { Location, TitleCasePipe } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import * as moment from "moment";
import { finalize } from "rxjs/operators";
import { Operation } from "src/app/shared/types/entity.type";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { QueryParams } from "../../../../core/http/query-params.type";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { UserInfo } from "../../../../shared/types/user-info";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { ContractService } from "../../contracts/contract.service";
import { Contract } from "../../contracts/contract.type";
import { PartnerService } from "../../partners/partner.service";
import { Partner, PartnerSpecialTypes } from "../../partners/partner.type";
import { AssetService } from "../asset.service";
import { Asset } from "../asset.type";
import { validateDates } from "../assets.shared";
import { CreateAssetService } from "../create-asset/create-asset.service";

@Component({
  selector: "app-create-asset",
  templateUrl: "./create-asset.component.html",
  styleUrls: ["./create-asset.component.css"],
  providers: [CreateAssetService, CompanyService, ContractService, TitleCasePipe],
})
export class CreateAssetComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  currentUser: UserInfo;
  loading$ = this.loader.loading$;
  questions: QuestionBase<string | boolean>[];
  partner: Partner;
  company: Company;
  operation: Operation;

  constructor(
    public location: Location,
    private router: Router,
    private assetService: AssetService,
    private loader: LoadingService,
    private notificationService: NotificationService,
    private questionsService: CreateAssetService,
    private authenticationService: AuthenticationService,
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private contractService: ContractService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.operation = Operation.create;
  }

  async ngOnInit(): Promise<void> {
    if (this.currentUser.partnerId && this.currentUser.isPartner()) {
      this.partner = await this.getPartner(this.currentUser.partnerId);
    }
    if (this.currentUser.companyId) {
      this.company = await this.getCompany(this.currentUser.selectedCompanyId || this.currentUser.companyId, {
        relations: { partner: true },
      });
    }

    const isRelutech =
      (this.partner?.specialType || this.company?.partner.specialType) === PartnerSpecialTypes.Relutech;

    this.questionsService
      .generateQuestions({
        currentUser: this.currentUser,
        ...(this.currentUser.isGlobal() && {
          partnerEdit: true,
        }),
        ...(this.currentUser.isPartner() && {
          isChannelWorksValue: false,
          partnerSkip: true,
        }),
        ...(this.currentUser.isCompany() && {
          companyValue: this.currentUser.selectedCompanyId || this.currentUser.companyId,
          partnerSkip: true,
          companySkip: true,
          isChannelWorksValue: false,
          ...(this.currentUser.hasManyCompanies() && {
            companySkip: false,
            partnerValue: this.currentUser.company.partnerId,
            companyEdit: false,
          }),
        }),
        hideContractNumber: true,
        hideQuotedSla: !isRelutech || this.currentUser.isCompany(),
        hideQuotedPrice: !isRelutech || this.currentUser.isCompany(),
        hideQuotedTerm: !isRelutech || this.currentUser.isCompany(),
        startDateHidden: true,
        endDateHidden: true,
        coverageStatusHidden: true,
        slaHidden: true,
        dmrHidden: true,
        monitoringHidden: true,
        channelWorksCostHidden: true,
        partnerPriceHidden: true,
        clientPriceHidden: true,
        internalProviderHidden: true,
        contractHidden: false,
        startDateEdit: false,
        showFmvValue: this.company?.enableAssetCustomFields && this.currentUser.isGlobalAdmin(),
        showBookValue: this.company?.enableAssetCustomFields && this.currentUser.isGlobalAdmin(),
        bookValueEdit: this.company?.enableAssetCustomFields && this.currentUser.isGlobalAdmin(),
        fmvValueEdit: this.company?.enableAssetCustomFields && this.currentUser.isGlobalAdmin(),
        operation: this.operation,
      })
      .subscribe((qs) => {
        this.questions = qs;
      });
  }

  navigateToBulkMode(): void {
    this.router.navigate(["/assets/bulk-upload"]);
  }

  async getPartner(partnerId: string, options?: QueryParams<Partner>): Promise<Partner> {
    return await this.partnerService.getPartner(partnerId, options).toPromise();
  }

  async getCompany(companyId: string, options?: QueryParams<Company>): Promise<Company> {
    return await this.companyService.getCompany(companyId, options).toPromise();
  }

  async getContract(contractId: string): Promise<Contract> {
    return await this.contractService.getContract(contractId, { relations: { maintenanceProvider: true } }).toPromise();
  }

  async onChange(event: any): Promise<void> {
    validateDates(event, this.dynamicForm);

    // show/hide quotedSla and quotedPrice
    if (event.source?.id === "partnerId") {
      const partnerId = event.value;
      const question = this.dynamicForm.questions.find((qs) => qs.key === "partnerId") as DropdownQuestion<Partner>;
      const specialType = question?.options.find((op) => op.value === partnerId).data?.specialType;
      const isRelutech = specialType === PartnerSpecialTypes.Relutech;

      this.dynamicForm.getQuestion("quotedSla").hide = !isRelutech;
      this.dynamicForm.getQuestion("quotedPrice").hide = !isRelutech;
      this.dynamicForm.getQuestion("quotedTerm").hide = !isRelutech;
    }

    if (event.source?.id === "companyId") {
      const companyId = event.value;
      this.company = await this.getCompany(companyId);
      if (this.company.enableAssetCustomFields && this.currentUser.isGlobalAdmin()) {
        this.dynamicForm.getQuestion("fairMarketValue").hide = false;
        this.dynamicForm.getQuestion("bookValue").hide = false;
        this.dynamicForm.form.controls["fairMarketValue"].enable();
        this.dynamicForm.form.controls["bookValue"].enable();
      } else {
        this.dynamicForm.getQuestion("fairMarketValue").hide = true;
        this.dynamicForm.getQuestion("bookValue").hide = true;
      }
    }

    if (event.source?.id === "contractId") {
      const contractId = event.value;
      const termsKeys = [
        "startDate",
        "endDate",
        "channelWorksCost",
        "partnerPrice",
        "clientPrice",
        "slaId",
        "dmr",
        "monitoring",
        "internalProviderId",
      ];

      if (contractId) {
        const selectedContract = await this.getContract(contractId);

        const { isPartnerPriceVisible, isPartnerPriceEnabled, isClientPriceVisible, isClientPriceEnabled } =
          this.questionsService;

        // ChannelWorks price
        if (this.currentUser.isGlobal()) {
          this.dynamicForm.getQuestion("channelWorksCost").hide = false;
          this.dynamicForm.form.controls["channelWorksCost"].setValue(undefined);
          this.dynamicForm.form.controls["channelWorksCost"].enable();
        }

        // partner price
        // handle visibility
        if (isPartnerPriceVisible(this.currentUser, this.company, selectedContract)) {
          this.dynamicForm.getQuestion("partnerPrice").hide = false;
        } else {
          this.dynamicForm.getQuestion("partnerPrice").hide = true;
          this.dynamicForm.form.controls["partnerPrice"].setValue(undefined);
        }
        // handle enabled/disabled
        if (isPartnerPriceEnabled(this.currentUser, this.company, selectedContract)) {
          this.dynamicForm.form.controls["partnerPrice"].enable();
        } else {
          this.dynamicForm.form.controls["partnerPrice"].disable();
        }
        // client price
        // handle visibility
        if (isClientPriceVisible(this.currentUser, this.company, selectedContract)) {
          this.dynamicForm.getQuestion("clientPrice").hide = false;
        } else {
          this.dynamicForm.getQuestion("clientPrice").hide = true;
          this.dynamicForm.form.controls["clientPrice"].setValue(undefined);
        }
        // handle enabled/disabled
        if (isClientPriceEnabled(this.currentUser, this.company, selectedContract)) {
          this.dynamicForm.form.controls["clientPrice"].enable();
        } else {
          this.dynamicForm.form.controls["clientPrice"].disable();
        }

        // Prices are excluded here due they have a different behavior
        const keysToExclude = ["channelWorksCost", "partnerPrice", "clientPrice"];

        termsKeys
          .filter((termKey) => !keysToExclude.includes(termKey))
          .forEach((termKey) => {
            if (termKey === "internalProviderId") {
              if (this.currentUser.isGlobal()) {
                this.dynamicForm.getQuestion(termKey).hide = false;
                this.dynamicForm.form.controls[termKey].enable();
                this.dynamicForm.form.controls[termKey].setValue(undefined);
              }
            } else if (termKey === "startDate" || termKey === "endDate") {
              this.dynamicForm.getQuestion(termKey).minDate = moment(selectedContract.startDate).toDate();
              this.dynamicForm.getQuestion(termKey).maxDate = moment(selectedContract.endDate).toDate();
              this.dynamicForm.getQuestion(termKey).hide = false;
              this.dynamicForm.form.controls[termKey].enable();
              this.dynamicForm.form.controls[termKey].setValue(undefined);
            } else {
              this.dynamicForm.getQuestion(termKey).hide = false;
              this.dynamicForm.form.controls[termKey].enable();
              this.dynamicForm.form.controls[termKey].setValue(undefined);
            }
          });
      } else {
        termsKeys.forEach((termKey) => {
          this.dynamicForm.getQuestion(termKey).hide = true;
          this.dynamicForm.form.controls[termKey].setValue(undefined);
          this.dynamicForm.form.controls[termKey].disable();
          this.dynamicForm.form.controls[termKey].markAsPristine();
        });
      }
    }

    if (event.source?.id === "coverageStatus") {
      const coverageStatusValue = event.value;
      this.dynamicForm.replaceOrAppendQuestion(
        this.questionsService.createDateQuestion({ coverageStatusValue, startDateEdit: true }),
      );
    }
  }

  onSubmit(values: Asset): void {
    for (const propName in values) {
      if (values[propName] === "") {
        delete values[propName];
      }
    }

    this.assetService
      .createAsset({
        ...values,
      })
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (asset: Asset) => {
          this.notificationService.success("Asset created");
          this.router.navigate([`/assets/${asset.id}`]);
        },
        (_error) => this.notificationService.error(_error || "Could not create Asset"),
      )
      .add(() => {
        this.loader.hide();
      });
  }

  goBack(): void {
    this.location.back();
  }

  isFormVisible(): boolean {
    return this.currentUser.isGlobal() || !!this.partner || !!this.company;
  }
}
